import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Logo } from "@/components/Layout";
import Footer from "@/components/Footer";
import DemoButton from "@/components/DemoButton";
import {
  LockKey, UsersThree, PaperPlaneTilt, ChartLineUp, ShieldCheck, Fingerprint,
  DeviceMobile, Check, ArrowRight, ListChecks, Scroll, HardDrives,
} from "@phosphor-icons/react";

export function MarketingShell({ children }) {
  return (
    <div className="min-h-screen grain relative">
      <header className="border-b border-zinc-200 bg-white/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <Link data-testid="nav-how" to="/how-it-works" className="px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">How it works</Link>
            <Link data-testid="nav-guide" to="/guide" className="px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Guide</Link>
            <Link data-testid="nav-security" to="/security" className="px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Security</Link>
            <Link data-testid="nav-pricing" to="/pricing" className="px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Pricing</Link>
            <Link data-testid="nav-signin" to="/auth" className="px-3 py-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors">Sign in</Link>
            <Link data-testid="nav-start" to="/auth?mode=register" className="px-4 py-2 text-sm font-semibold bg-black text-white rounded-md hover:bg-zinc-800 transition-colors active:scale-95">Start free</Link>
          </nav>
        </div>
      </header>
      <main className="relative z-10">{children}</main>
      <Footer />
    </div>
  );
}

function PageHead({ tag, title, sub }) {
  return (
    <section className="max-w-6xl mx-auto px-6 pt-20 pb-10">
      <div className="text-xs font-mono uppercase tracking-widest text-[#1F6FE5] mb-4">{tag}</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98] max-w-3xl">{title}</h1>
      {sub && <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mt-6">{sub}</p>}
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    { icon: UsersThree, t: "1 · Set up your election", b: "Create positions and candidates, then add your voter roll by CSV import, one-by-one, or a self-registration link. Nominations can even be collected first and approved into candidates." },
    { icon: PaperPlaneTilt, t: "2 · Invite voters", b: "Each voter gets a cryptographic single-use link by email or SMS. No accounts, no passwords — they tap and vote. Feature-phone voters dial a USSD code; in-person voters use a kiosk code." },
    { icon: DeviceMobile, t: "3 · Voters cast a secret ballot", b: "A phone-first ballot handles single-choice or ranked (Borda). Double-voting is blocked atomically, and the ballot is stored with no link to the voter." },
    { icon: ChartLineUp, t: "4 · Watch turnout live", b: "A live board shows turnout split by web, USSD and kiosk in real time — shareable with your electoral committee." },
    { icon: ListChecks, t: "5 · Close & publish results", b: "Results compute instantly with clear winners and tie handling. Export results, the voter roll, and a one-click integrity report as PDF." },
  ];
  return (
    <MarketingShell>
      <PageHead tag="How it works" title={<>From voter list to <span className="text-[#1F6FE5]">verified result</span> in an afternoon.</>}
        sub="SelectFlow runs the whole lifecycle — registration, nominations, secure voting across web, USSD and kiosk, live turnout, and an integrity report you can hand to any committee." />
      <section className="max-w-6xl mx-auto px-6 pb-8">
        <div className="grid gap-5">
          {steps.map((s) => (
            <div key={s.t} className="flex gap-5 bg-white border border-zinc-200 p-6 hover:-translate-y-0.5 hover:shadow-md transition-all">
              <s.icon size={30} weight="duotone" className="text-[#1F6FE5] shrink-0 mt-1" />
              <div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-1">{s.t}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed max-w-2xl">{s.b}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-12">
          <DemoButton className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#1F6FE5] text-white rounded-md font-semibold hover:bg-[#1a5fc4] transition-colors active:scale-95" label="Explore a live demo" />
          <Link to="/auth?mode=register" className="inline-flex items-center gap-2 px-6 py-3.5 border border-zinc-300 rounded-md font-semibold hover:border-black transition-colors">Start free <ArrowRight size={18} weight="bold" /></Link>
        </div>
      </section>
    </MarketingShell>
  );
}

export function Security() {
  const points = [
    { icon: LockKey, t: "Anonymity by design", b: "Ballots carry no voter identifier — not a setting you can forget, but a property of the data model. Turnout and results are provable without ever linking a vote to a person." },
    { icon: Fingerprint, t: "One-time secure links", b: "Every voter receives a single-use cryptographic token. Reuse is rejected and double-voting is blocked atomically, even under concurrent load." },
    { icon: Scroll, t: "Tamper-evident audit trail", b: "Every action is recorded in an append-only hash chain. Any edit or deletion breaks the chain — and the one-click integrity report shows VERIFIED or flags exactly where it broke." },
    { icon: ShieldCheck, t: "Integrity checks & receipts", b: "Orphaned-claim reconciliation, flagged-vote detection around admin actions, and per-voter ballot receipts give committees independent evidence the count is sound." },
    { icon: UsersThree, t: "Strict multi-tenant isolation", b: "Every organisation's data is scoped by tenant. Admins see only their own elections; a confined super-admin can never read tenant ballots or results." },
    { icon: HardDrives, t: "Secure sessions", b: "httpOnly, Secure, SameSite cookies; hashed credentials; rate-limited endpoints; and TOTP two-factor for platform administrators." },
  ];
  return (
    <MarketingShell>
      <PageHead tag="Security & integrity" title={<>Built for elections people <span className="text-[#1F6FE5]">actually trust.</span></>}
        sub="Trust isn't a marketing word here — it's enforced in the schema and provable on paper. Here's exactly how SelectFlow protects a vote." />
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {points.map((p) => (
            <div key={p.t} className="bg-white border border-zinc-200 p-7">
              <p.icon size={30} weight="duotone" className="text-[#1F6FE5] mb-4" />
              <h3 className="font-display font-bold text-xl tracking-tight mb-2">{p.t}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
        <div className="mt-12"><DemoButton className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#1F6FE5] text-white rounded-md font-semibold hover:bg-[#1a5fc4] transition-colors active:scale-95" label="See the integrity report in the demo" /></div>
      </section>
    </MarketingShell>
  );
}

export function Pricing() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/public/pricing").then(({ data }) => setP(data)).catch(() => {}); }, []);
  const cur = p?.currency || "GHS";
  const g = (v) => `${cur} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

  // Estimator: slider maps to nice roll sizes; price mirrors backend price_for_roll (banded).
  const STEPS = [50, 100, 150, 250, 400, 500, 750, 1000, 1500, 2000, 3000, 5000, 7500, 10000, 15000, 20000];
  const [idx, setIdx] = useState(4); // default 400 voters
  const roll = STEPS[idx];
  const estimate = useMemo(() => {
    const bands = p?.per_election_bands || [];
    for (const b of bands) {
      if (b.upto == null) return { quote: true, band: b.label };
      if (roll <= b.upto) {
        if (b.per_voter == null) return { amount: Number(b.flat_min || 0), band: b.label };
        const amt = Math.max(Number(b.flat_min || 0), Math.round(b.per_voter * roll * 100) / 100);
        return { amount: amt, band: b.label };
      }
    }
    return { quote: true, band: "15,000+" };
  }, [roll, p]);

  return (
    <MarketingShell>
      <PageHead tag="Pricing" title={<>Transparent pricing. <span className="text-[#1F6FE5]">Published.</span></>}
        sub="Most vendors hide their prices. We don't. Start free with a real end-to-end election, pay per election as you scale, or run a whole year on a subscription." />

      <section className="max-w-6xl mx-auto px-6 pb-6">
        {/* Free + Annual highlight cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div data-testid="plan-free" className="bg-white border border-zinc-200 p-7">
            <h3 className="font-display font-black text-2xl tracking-tight">Free</h3>
            <div className="text-3xl font-display font-black mt-2 mb-1">{g(0)}</div>
            <p className="text-sm text-zinc-500 mb-5">{p?.free?.note}</p>
            <ul className="space-y-2 mb-7">
              {[`Up to ${p?.free?.voters ?? 100} voters`, `${p?.free?.elections ?? 1} active election`, "Web, USSD & kiosk voting", "Live turnout board", 'Integrity report (watermarked "Trial")'].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-700"><Check size={16} weight="bold" className="text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/auth?mode=register" data-testid="plan-cta-free" className="block text-center px-4 py-3 rounded-md text-sm font-semibold border border-zinc-300 hover:border-black transition-colors">Start free</Link>
          </div>
          <div data-testid="plan-annual" className="bg-[#0B1220] text-white p-7 relative overflow-hidden">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7CA9F9] mb-2">Annual subscription</div>
            <h3 className="font-display font-black text-2xl tracking-tight">Run a whole year of votes</h3>
            <div className="text-3xl font-display font-black mt-2 mb-1">{p ? g(p.annual.price) : "—"}<span className="text-sm font-medium text-zinc-400"> /year</span></div>
            <p className="text-sm text-zinc-300 mb-5">Up to {p?.annual?.elections} elections · {p?.annual?.voters_each?.toLocaleString()} voters each. {p?.annual?.note}</p>
            <p className="text-xs text-zinc-400 mb-6">Ideal for professional bodies and churches running several committee/board elections a year — infrastructure, not a one-off.</p>
            <a href="mailto:info@iassistpro.com?subject=SelectFlow annual subscription" data-testid="plan-cta-annual" className="block text-center px-4 py-3 rounded-md text-sm font-semibold bg-[#1F6FE5] hover:bg-[#1a5fc4] transition-colors">Talk to us</a>
          </div>
        </div>
      </section>

      {/* Price estimator */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-6">
        <div data-testid="price-estimator" className="bg-[#0B1220] text-white p-7 sm:p-9 relative overflow-hidden">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7CA9F9] mb-2">Estimate your election</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display font-black text-2xl tracking-tight mb-1">How many voters?</h2>
              <p className="text-sm text-zinc-300 mb-7">Drag to size your roll — we'll show the per-election price instantly. No hidden fees.</p>
              <div className="flex items-baseline gap-3 mb-6">
                <span data-testid="estimator-roll" className="font-display font-black text-4xl tracking-tighter tabular-nums">
                  {roll >= 20000 ? "15,000+" : roll.toLocaleString()}
                </span>
                <span className="text-sm text-zinc-400">voters</span>
              </div>
              <Slider
                data-testid="estimator-slider"
                value={[idx]}
                onValueChange={(v) => setIdx(v[0])}
                min={0}
                max={STEPS.length - 1}
                step={1}
                className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-[#1F6FE5] [&_.bg-primary]:bg-[#1F6FE5] [&_.bg-primary\\/20]:bg-white/20"
              />
              <div className="flex justify-between text-[11px] text-zinc-500 mt-3">
                <span>50</span><span>2,000</span><span>15,000+</span>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-7 text-center">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">Estimated price</div>
              {estimate.quote ? (
                <>
                  <div data-testid="estimator-price" className="font-display font-black text-4xl tracking-tighter mb-1">Custom quote</div>
                  <p className="text-sm text-zinc-400 mb-5">Rolls above 15,000 get a tailored institutional quote.</p>
                  <a href="mailto:info@iassistpro.com?subject=SelectFlow quote for large roll" className="inline-block px-6 py-3 rounded-md text-sm font-semibold bg-[#1F6FE5] hover:bg-[#1a5fc4] transition-colors">Request a quote</a>
                </>
              ) : (
                <>
                  <div data-testid="estimator-price" className="font-display font-black text-5xl tracking-tighter mb-1 tabular-nums">{g(estimate.amount)}</div>
                  <p className="text-sm text-zinc-400 mb-2">one-off, for this election</p>
                  <p data-testid="estimator-band" className="text-xs text-zinc-500 mb-6">{estimate.band} band{estimate.amount === 300 ? " · flat minimum" : ` · ${cur} ${(estimate.amount / roll).toFixed(2)}/voter`}</p>
                  <Link to="/auth?mode=register" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold bg-[#1F6FE5] hover:bg-[#1a5fc4] transition-colors">Start this election <ArrowRight size={16} weight="bold" /></Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Per-election banded table */}
      <section className="max-w-6xl mx-auto px-6 pb-6">
        <h2 className="font-display font-bold text-2xl tracking-tight mb-1">Per election</h2>
        <p className="text-sm text-zinc-500 mb-5">Charged on your roll size when you publish. The bigger the roll, the lower the per-voter rate.</p>
        <div className="bg-white border border-zinc-200 overflow-hidden" data-testid="pricing-bands">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-400 text-xs uppercase tracking-widest border-b border-zinc-200">
                <th className="p-4 font-semibold">Roll size</th>
                <th className="p-4 font-semibold">Per voter</th>
                <th className="p-4 font-semibold">Example</th>
              </tr>
            </thead>
            <tbody>
              {(p?.per_election_bands || []).map((b) => (
                <tr key={b.label} className="border-b border-zinc-100 last:border-0" data-testid={`band-${b.upto || "top"}`}>
                  <td className="p-4 font-medium">{b.label}</td>
                  <td className="p-4 text-zinc-700">{b.per_voter == null ? (b.flat_min ? `${g(b.flat_min)} min` : "Quote") : `${cur} ${b.per_voter.toFixed(2)}`}</td>
                  <td className="p-4 text-zinc-500">{b.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/auth?mode=register" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1F6FE5] text-white rounded-md font-semibold hover:bg-[#1a5fc4] transition-colors">Start your election <ArrowRight size={17} weight="bold" /></Link>
          <DemoButton />
        </div>
      </section>

      {/* Institutional (contact-only) */}
      <section className="max-w-6xl mx-auto px-6 pb-6">
        <div className="bg-white border-2 border-[#1F6FE5] p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#1F6FE5] mb-2">Institutional · Certified election</div>
              <h3 className="font-display font-black text-2xl tracking-tight">We run it for you, end to end</h3>
              <p className="text-sm text-zinc-600 mt-2">From {p ? g(p.institutional.from) : "—"} — fully-managed, certified elections with a signed integrity report. Indicative: {(p?.institutional?.tiers || []).filter(t => t.price).map(t => `${t.label} ${g(t.price)}`).join(" · ")}.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-4">
                {(p?.institutional?.includes || []).map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm text-zinc-700"><Check size={15} weight="bold" className="text-emerald-500 mt-0.5 shrink-0" /> {f}</div>
                ))}
              </div>
            </div>
            <a href="mailto:info@iassistpro.com?subject=SelectFlow certified election" data-testid="plan-cta-institutional" className="px-6 py-3 rounded-md text-sm font-semibold bg-[#1F6FE5] text-white hover:bg-[#1a5fc4] transition-colors whitespace-nowrap">Request a quote</a>
          </div>
        </div>
      </section>

      {/* Diaspora + add-ons + policies */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-white border border-zinc-200 p-6">
            <h4 className="font-display font-bold text-lg tracking-tight mb-2">Diaspora / international</h4>
            <p className="text-sm text-zinc-600">Billed in USD for alumni & diaspora associations: <span className="font-semibold">${p?.diaspora?.per_voter_low?.toFixed(2)}–${p?.diaspora?.per_voter_high?.toFixed(2)}</span> per voter, ${p?.diaspora?.minimum} minimum. Well below international incumbents.</p>
          </div>
          <div className="bg-white border border-zinc-200 p-6">
            <h4 className="font-display font-bold text-lg tracking-tight mb-2">Add-ons</h4>
            <ul className="space-y-2">
              {(p?.addons || []).map((a) => (
                <li key={a.name} className="text-sm text-zinc-600"><span className="font-medium text-zinc-800">{a.name}:</span> {a.detail}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-zinc-200 p-6">
            <h4 className="font-display font-bold text-lg tracking-tight mb-2">Discounts</h4>
            <ul className="space-y-2">
              {(p?.policies || []).map((d) => (
                <li key={d} className="flex items-start gap-2 text-sm text-zinc-600"><Check size={15} weight="bold" className="text-emerald-500 mt-0.5 shrink-0" /> {d}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mt-6">Prices in {cur} (a USD line is available for diaspora customers) and exclude any applicable taxes. Need something bespoke? <a href="mailto:info@iassistpro.com" className="text-[#1F6FE5] hover:underline">Talk to us</a>.</p>
      </section>
    </MarketingShell>
  );
}
